import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_SECTIONS = [
  {
    key: 'photos',
    title: 'Member Photos',
    rule: 'Required. At least 2 portrait photos (4 if joining as a couple).',
    minCount: 1,
    minLabel: null,
    items: [
      { key: 'photo',          label: 'Portrait Photos',  desc: 'At least 2 clear portrait photos of yourself (4 photos if joining as a couple).', accept: 'image/*',       max: 4, required: true },
    ],
  },
  {
    key: 'police',
    title: 'Police Check',
    rule: 'Required. Must be dated within the past 24 months.',
    minCount: 1,
    minLabel: null,
    items: [
      { key: 'police_check',   label: 'Police Check',     desc: 'National Police Check — must be dated within the past 24 months.',                 accept: 'image/*,.pdf',  max: 1, required: true },
    ],
  },
  {
    key: 'primary_id',
    title: 'Primary Photo ID',
    rule: 'Provide ONE of the following (Passport OR Driver\'s Licence OR National ID).',
    minCount: 1,
    minLabel: 'Upload at least 1 primary photo ID.',
    items: [
      { key: 'passport',        label: 'Current Passport',          desc: 'Photo page of your current passport.',                                       accept: 'image/*,.pdf',  max: 1 },
      { key: 'drivers_licence', label: "Current Driver's Licence",  desc: "Front and back of your current driver's licence.",                           accept: 'image/*,.pdf',  max: 2 },
      { key: 'national_id',     label: 'Current National ID',       desc: 'Any current government-issued national identity document.',                  accept: 'image/*,.pdf',  max: 2 },
    ],
  },
  {
    key: 'supporting',
    title: 'Supporting ID (optional but recommended)',
    rule: 'Any additional ID helps us verify you faster.',
    minCount: 0,
    minLabel: null,
    items: [
      { key: 'seniors_concession_card', label: 'Seniors Concession Card',          desc: 'Both sides of your current Seniors Concession Card.',                            accept: 'image/*,.pdf', max: 2 },
      { key: 'club_card',               label: 'Club or Library Membership Card',  desc: 'Any current membership card (club, library, etc.) for additional identity.',     accept: 'image/*,.pdf', max: 2 },
    ],
  },
];

// Flattened for convenience (uploads, counting)
const DOC_TYPES = DOC_SECTIONS.flatMap(s => s.items);

const VerificationDocs = ({ getAuthConfig, appStatus, docKeys, title, intro }) => {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState({});
  const [error, setError] = useState('');
  const [profileMsg, setProfileMsg] = useState('');

  const fetchDocs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/members/verification-docs`, getAuthConfig());
      setDocs(data);
    } catch (e) { console.error('fetchDocs error:', e); }
  }, [getAuthConfig]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (docType, file) => {
    setError(''); setProfileMsg('');
    setUploading(u => ({ ...u, [docType]: true }));
    const form = new FormData();
    form.append('doc_type', docType);
    form.append('file', file);
    try {
      const config = getAuthConfig();
      await axios.post(`${API}/members/verification-docs`, form, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      await fetchDocs();
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed. Please try again.');
    }
    setUploading(u => ({ ...u, [docType]: false }));
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      await axios.delete(`${API}/members/verification-docs/${docId}`, getAuthConfig());
      await fetchDocs();
    } catch { setError('Failed to delete document.'); }
  };

  const handleSetProfilePhoto = async (docId) => {
    try {
      await axios.patch(`${API}/members/verification-docs/${docId}/set-profile-photo`, {}, getAuthConfig());
      setProfileMsg('Profile photo updated successfully!');
      setTimeout(() => setProfileMsg(''), 3000);
    } catch { setError('Failed to set profile photo.'); }
  };

  const isApproved = appStatus?.status === 'approved';

  // Compute section completion (optionally restricted to a subset of document types)
  const activeSections = docKeys
    ? DOC_SECTIONS.map(s => ({ ...s, items: s.items.filter(i => docKeys.includes(i.key)) })).filter(s => s.items.length)
    : DOC_SECTIONS;
  const sectionStatus = activeSections.map(section => {
    const uploadedTypes = section.items.filter(it => docs.some(d => d.doc_type === it.key && (section.key !== 'mandatory' || it.required ? true : true)));
    let complete = false;
    if (section.items.some(i => i.required)) {
      complete = section.items.filter(i => i.required).every(it => docs.some(d => d.doc_type === it.key));
    } else if (section.key === 'supporting') {
      // Supporting is "complete" only once at least one supporting doc is uploaded
      complete = uploadedTypes.length > 0;
    } else {
      complete = uploadedTypes.length >= section.minCount;
    }
    return { ...section, uploadedCount: uploadedTypes.length, complete };
  });
  const allMandatoryMet = sectionStatus.filter(s => s.key !== 'supporting').every(s => s.complete);
  const totalUploaded = docs.filter(d => !docKeys || docKeys.includes(d.doc_type)).length;
  const requiredLabel = sectionStatus.map(s => s.title).join(' + ');

  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }} data-testid="verification-docs-section">
      <h3 style={{ margin: '0 0 6px', color: '#1a2332' }}>{title || 'Verification Documents'}</h3>
      <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 14px' }}>
        {intro || 'Upload your identity documents below. These are kept private and used only for member verification.'}
        {!isApproved && ' Once approved, you can select a photo as your public profile picture.'}
      </p>

      {/* Progress banner */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '12px 16px', borderRadius: '8px', marginBottom: '18px',
        background: allMandatoryMet ? '#d1fae5' : '#fef3c7',
        border: `1px solid ${allMandatoryMet ? '#86efac' : '#fde68a'}`,
        color: allMandatoryMet ? '#065f46' : '#92400e', fontSize: '14px'
      }} data-testid="verification-progress-banner">
        <span style={{ fontWeight: 700 }}>{allMandatoryMet ? '✓ All required documents uploaded' : `Still required: ${requiredLabel}`}</span>
        <span style={{ marginLeft: 'auto', fontSize: '12px' }}>{totalUploaded} file{totalUploaded === 1 ? '' : 's'} uploaded</span>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', color: '#b91c1c', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}
      {profileMsg && <div style={{ background: '#d1fae5', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 14px', color: '#059669', marginBottom: '16px', fontSize: '14px' }}>{profileMsg}</div>}

      {sectionStatus.map(section => (
        <div key={section.key} style={{ marginBottom: '24px' }} data-testid={`doc-section-${section.key}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h4 style={{ margin: 0, color: '#1a2332', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{section.title}</h4>
            {section.complete ? (
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#059669', background: '#d1fae5', padding: '2px 10px', borderRadius: '12px' }}>✓ Complete</span>
            ) : section.key !== 'supporting' ? (
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '2px 10px', borderRadius: '12px' }}>Required</span>
            ) : (
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', background: '#f3f4f6', padding: '2px 10px', borderRadius: '12px' }}>Optional</span>
            )}
          </div>
          <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 12px' }}>{section.rule}</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {section.items.map(({ key, label, desc, accept, max }) => {
              const myDocs = docs.filter(d => d.doc_type === key);
              const canUploadMore = myDocs.length < max;
              return (
                <div key={key} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '14px', color: '#1a2332' }}>{label}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{desc}</div>
                    </div>
                    {myDocs.length > 0 && (
                      <span style={{ fontSize: '12px', fontWeight: '700', color: '#059669', background: '#d1fae5', padding: '2px 10px', borderRadius: '12px' }}>
                        {myDocs.length}/{max} uploaded
                      </span>
                    )}
                  </div>
                  {myDocs.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                      {myDocs.map(doc => (
                        <div key={doc.doc_id} style={{ position: 'relative', background: '#f8fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {doc.resource_type === 'image' ? (
                            <img src={doc.url} alt={doc.label} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: '48px', height: '48px', background: '#fee2e2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>📄</div>
                          )}
                          <div>
                            <div style={{ fontSize: '12px', color: '#374151', fontWeight: '600', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.filename}</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>{new Date(doc.uploaded_at).toLocaleDateString()}</div>
                            <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                              {key === 'photo' && isApproved && (
                                <button onClick={() => handleSetProfilePhoto(doc.doc_id)}
                                  style={{ fontSize: '11px', padding: '2px 8px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                  data-testid={`set-profile-photo-${doc.doc_id}`}>
                                  Use as Profile
                                </button>
                              )}
                              <button onClick={() => handleDelete(doc.doc_id)}
                                style={{ fontSize: '11px', padding: '2px 8px', background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer' }}
                                data-testid={`delete-doc-${doc.doc_id}`}>
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {canUploadMore && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: '#1a2332', color: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
                      data-testid={`upload-${key}-btn`}>
                      {uploading[key] ? 'Uploading...' : `+ Upload ${label}`}
                      <input type="file" accept={accept} style={{ display: 'none' }}
                        disabled={uploading[key]}
                        onChange={e => { if (e.target.files?.[0]) handleUpload(key, e.target.files[0]); e.target.value = ''; }} />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {!isApproved && (
        <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '16px', borderTop: '1px solid #f3f4f6', paddingTop: '12px' }}>
          Once your application is approved, you will be able to select one of your photos as your public member profile photo.
        </p>
      )}
    </div>
  );
};

export default VerificationDocs;
